// The budget-inclusive text cut, once — the arithmetic thirteen producers wrote by
// hand and every one of them wrote the same way.
//
// `${text.slice(0, MAX)}…` is MAX + 1 characters. A schema bound of MAX then
// rejects exactly the strings the producer meant to fit, and the producer's own
// `max` constant is the one it disagrees with. `@developerz/domain`'s `hiveCut`
// found that first and fixed it for the hive lanes; this is the same rule with no
// lane in its name, so a caller with no hive event to write can still have it.
//
// MEASURED, 2026-09-09, `scripts/one-impl.test.ts` `ellipsis-cut`, 2,384 production
// files: 25 hand-rolled copies of this cut. Thirteen of them were identical in
// effect and were spelled `refusalQuote`, `safeLabel`, `cap`, `oneLine` (twice),
// `sanitizeForEvent`, `safe`, `stageReason`, `clamp` (twice), `excerpt`,
// `renderPriorReason` and `neuterForComment` — thirteen names for one function,
// which is why the ratchet's detector is keyed on the SHAPE and not on any name.

/**
 * The bound {@link ellipsize} cuts to. An OBJECT, and that is the whole point.
 *
 * `Array.prototype.map` calls back with `(value, index, array)`, so `paths.map(cut)`
 * hands the INDEX in as the bound — `'src/ghost.ts:9'` comes back as `'…'`, every
 * element truncated to nothing. That is not hypothetical: it fired in this repo
 * against a bare `number` bound and a test caught it, which is the system working,
 * but the SIGNATURE invited it and the next caller writes the same line. A `number`
 * is not a {@link CutBound}, so the mistake cannot compile.
 */
export type CutBound = { readonly max: number };

/**
 * The first `end` UTF-16 code units of `text`, or `end - 1` when `end` would split a
 * surrogate pair (an emoji, any character outside the BMP). A lone surrogate is not text:
 * PostgreSQL's `jsonb` refuses it (22P02, "Unicode low surrogate must follow a high
 * surrogate"), so a cut through a pair turns valid input into a failed write (#6767, the
 * review on #7667). Never longer than `end`, so a caller's length bound still holds.
 */
export function sliceWhole(text: string, end: number): string {
  return text.slice(0, wholeEnd(text, end));
}

/** The index {@link sliceWhole} cuts at: `end` clamped to `[0, text.length]`, one less inside a pair. */
function wholeEnd(text: string, end: number): number {
  if (end <= 0) return 0;
  if (end >= text.length) return text.length;
  const last = text.charCodeAt(end - 1);
  return last >= 0xd800 && last <= 0xdbff ? end - 1 : end;
}

/**
 * Cut `text` so the RESULT is at most `bound.max` characters — the ellipsis counts
 * against the budget. Text already within the bound is returned unchanged, so the
 * value handed to a `z.string().max(bound.max)` always fits, cut or not.
 *
 * A bound below 2 THROWS, naming the value. That is the same defect arriving
 * through a COMPUTED bound, which no signature can refuse, and the floor is a
 * property rather than a tuned number: under 2 there is no room for a single
 * character beside the ellipsis, so the result is the ellipsis ALONE — a deletion
 * wearing a truncation's shape.
 *
 * The cut never splits a surrogate pair ({@link sliceWhole}), so the result may be one
 * character shorter than `bound.max`, never longer.
 *
 * It does NOT trim, collapse whitespace or redact. Callers that need those do them
 * FIRST and for their own reasons — redacting after a cut can leave the half of a
 * secret that no pattern still matches, and collapsing after a cut spends budget on
 * whitespace that was going to vanish.
 */
export function ellipsize(text: string, bound: CutBound): string {
  const { max } = bound;
  if (!Number.isInteger(max) || max < 2) {
    throw new Error(
      `ellipsize: max must be an integer >= 2, got ${max}. Under 2 there is no room ` +
        `for a character beside the ellipsis, so every value is deleted rather than ` +
        `cut; the usual cause is a computed bound that went negative.`,
    );
  }
  return text.length <= max ? text : `${text.slice(0, wholeEnd(text, max - 1))}…`;
}

/**
 * Cut `text` to at most `bound.max` characters by keeping its HEAD and TAIL around a
 * marker naming the cut — the marker counted against the budget, the same rule as
 * {@link ellipsize}. One third head, two thirds tail: a command's verdict, a test
 * runner's failure and a compiler's last error print at the END, and a head-only cut
 * drops exactly what the reader came for. Text already within the bound is returned
 * unchanged.
 *
 * `marker(elided)` renders the gap for `elided` dropped characters. It is budgeted at
 * its widest — `elided` as large as the whole text — so the real marker, whose count
 * is never larger, always fits. A bound too small to hold the marker plus one head and
 * one tail character THROWS, naming the value: the bound is a caller's constant, and
 * a cut that returned the marker alone would be a deletion wearing a cut's shape.
 *
 * MEASURED 2026-10-09: two copies (`packages/tools/src/code/output-bounds.ts`
 * `clipStream`, `packages/agent/src/compaction-transcript.ts` `clipHeadTail`), the
 * second adding its marker ON TOP of the bound. `scripts/one-impl-concerns.ts`
 * `head-tail-clip` counts the shape.
 */
export function clipHeadTail(
  text: string,
  bound: CutBound,
  marker: (elided: number) => string,
): string {
  const { max } = bound;
  if (text.length <= max) return text;
  const keep = max - marker(text.length).length;
  if (!Number.isInteger(keep) || keep < 3) {
    throw new Error(
      `clipHeadTail: max ${max} leaves ${keep} chars beside the marker; it needs at least 3 ` +
        '(a head third and a tail). The bound is a constant, so this is a code bug, not input.',
    );
  }
  const head = Math.floor(keep / 3);
  const tail = keep - head;
  return `${text.slice(0, head)}${marker(text.length - keep)}${text.slice(text.length - tail)}`;
}
